// The one registry of TanStack Query keys (issue: architecture
// deepening). Before this file every screen typed its own key literal;
// now a key exists exactly once, here, and every query/mutation
// invalidation goes through these factories. Rules the factories
// encode:
//
// - One cached shape per resource. ['me','connections'] holds ONE
//   shape -- an infinite-query pages object -- for every reader; flat
//   consumers read it through lib/connections-cache.ts's selectors
//   (useAllConnections), never a sibling literal. Never add a sibling
//   key whose prefix overlaps another resource's root.
// - Optional-argument factories double as the invalidation root:
//   threads() -> ['threads'] matches every category key the way
//   TanStack's prefix semantics want; thread(id) -> ['thread', id]
//   and thread() -> ['thread'] match the replies key
//   ['thread', id, 'posts'] in the same direction.
import type { ThreadCategory } from '@/lib/api';

export const queryKeys = {
  // ['me'] root -- what use-tier0-state / profile invalidate after a
  // change to the current account. ['me', userId] is use-auth-state's
  // session-scoped /me query (keyed on user id so an hourly token
  // refresh never invalidates it).
  me: () => ['me'] as const,
  meSession: (userId: string | undefined) => ['me', userId] as const,
  meProfile: () => ['me', 'profile'] as const,
  revealRequests: () => ['me', 'reveal-requests'] as const,
  // ['me','activity'] -- the app-wide badge poller's one entry (issue
  // #395). Tab badges, the threads banner, and settings all read it;
  // mark-read and reveal-request resolution invalidate it so a badge
  // the user just acted on doesn't wait out the 30s poll.
  activity: () => ['me', 'activity'] as const,
  connections: () => ['me', 'connections'] as const,
  notificationPreferences: () => ['me', 'notification-preferences'] as const,
  blockedUsers: () => ['me', 'blocks'] as const,
  safetyReminderSeen: () => ['safety-reminder-seen'] as const,
  previewThreads: () => ['preview-threads'] as const,
  threads: (category?: ThreadCategory) =>
    category === undefined ? (['threads'] as const) : (['threads', category] as const),
  thread: (threadId?: string) => (threadId === undefined ? (['thread'] as const) : (['thread', threadId] as const)),
  threadPosts: (threadId: string) => ['thread', threadId, 'posts'] as const,
  profile: (userId: string) => ['profile', userId] as const,
  messages: (connectionId: string) => ['messages', connectionId] as const,
  photo: (path: string) => ['photo', path] as const,
};
